import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import raceApi from '../../clients/RaceClient';
import type { RaceNight } from '../../models/raceNights/RaceNight';
import { dateParts, isActionableNight, nightPill, windowLabel } from '../../utils/raceNightFormat';
import { VENUES } from '../raceNights/RaceNights';
import { PILL_CLASS } from '../raceNights/RaceNightRow';
// The page's own pill colours, so a night reads the same here and there.
import '../raceNights/RaceNights.css';

const MAX_ROWS = 3;

interface Props {
  /** A VENUES key, or '' when the driver has no preferred venue yet: then nothing is fetched. */
  location: string;
  onLocationChange: (location: string) => void;
  /** undefined while the membership is still loading: the section keeps its placeholders. */
  isMember: boolean | undefined;
}

/** A night on the home: date tile, title, the time on one line, and the Race nights page's pill. */
const HomeNightRow: React.FC<{ night: RaceNight; to: string }> = ({ night, to }) => {
  const p = dateParts(night.date);
  const pill = nightPill(night);
  const rig = night.mine === 'seated' && night.myRig ? ` · ${night.myRig === 'esports' ? 'Esports' : 'Pro'} rig` : '';
  return (
    <Link to={to} className="home-rn-row" data-testid={`home-night-${night.occurrenceId}`}>
      <span className="home-rn-tile" aria-hidden="true">
        <span className="home-rn-dow">{p.dow}</span>
        <span className="home-rn-day">{p.day}</span>
        <span className="home-rn-dow">{p.month}</span>
      </span>
      <span className="home-rn-info">
        <span className="home-rn-title">{night.title}</span>
        <span className="home-rn-when">{windowLabel(night.startTime, night.endTime)}{rig}</span>
      </span>
      {/* On the home a card price is a pill too (the page shows it as a plain figure). */}
      <span className={pill.tone === 'price' ? 'rn-pill home-rn-price' : PILL_CLASS[pill.tone]}>{pill.label}</span>
    </Link>
  );
};

/** The Race nights page at this venue, so a row opens the list it came from, not the preferred venue's. */
const pageAt = (location: string) => (location ? `/race-nights?venue=${encodeURIComponent(location)}` : '/race-nights');

/**
 * A night to show on the home: one the driver holds a place on, or one they can claim RIGHT NOW. A full
 * night they don't hold is only a waitlist, which the page (not the home) offers.
 */
const isOfferedHere = (night: RaceNight, isMember: boolean | undefined): boolean =>
  isActionableNight(night)
  && (night.mine !== 'none' || night.seats.open > 0)
  && (!!isMember || night.access === 'public');

/**
 * The home's race nights at one venue: the nights this driver holds or can claim, at most three. A
 * non-member sees Public nights only, each at its card price. It holds its final height with three
 * placeholder rows until both the list and the membership are known, so nothing below it jumps.
 */
const RaceNightsSection: React.FC<Props> = ({ location, onLocationChange, isMember }) => {
  const [nights, setNights] = useState<RaceNight[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setNights(null);
    setFailed(false);
    if (!location) return undefined;
    let cancelled = false;
    // Promise.resolve().then so a client that throws synchronously can't take the home down.
    Promise.resolve()
      .then(() => raceApi.listRaceNights(location))
      .then((n) => { if (!cancelled) setNights(n ?? []); })
      .catch(() => { if (!cancelled) { setNights([]); setFailed(true); } });
    return () => { cancelled = true; };
  }, [location]);

  const loading = !!location && (nights === null || isMember === undefined);
  const shown = (nights ?? [])
    .filter((n) => isOfferedHere(n, isMember))
    .slice(0, MAX_ROWS);

  let body: React.ReactNode;
  if (!location) {
    body = <p className="home-rn-note">Pick a venue to see its race nights.</p>;
  } else if (loading) {
    body = Array.from({ length: MAX_ROWS }, (_, i) => (
      <div key={i} className="home-rn-skeleton" data-testid="rn-skeleton" aria-hidden="true" />
    ));
  } else if (shown.length === 0) {
    body = (
      <p className="home-rn-note">
        {failed ? 'Couldn’t load race nights just now.' : 'No race nights you can claim right now.'}{' '}
        <Link to={pageAt(location)}>See all ›</Link>
      </p>
    );
  } else {
    body = (
      <>
        {shown.map((n) => <HomeNightRow key={n.occurrenceId} night={n} to={pageAt(location)} />)}
        {!isMember && <p className="home-rn-note">Public nights only. Buy a seat here, and bring friends.</p>}
      </>
    );
  }

  return (
    <section className="home-rn" aria-labelledby="home-rn-title" aria-busy={loading}>
      <div className="home-rn-head">
        <div className="home-rn-head-left">
          <h2 id="home-rn-title" className="home-rn-heading">Race nights</h2>
          <select
            className="home-rn-venue"
            aria-label="Race nights venue"
            value={location}
            onChange={(e) => onLocationChange(e.target.value)}
          >
            {!location && <option value="" disabled>Pick a venue</option>}
            {VENUES.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
          </select>
        </div>
        <Link to={pageAt(location)} className="home-rn-all">See all race nights ›</Link>
      </div>
      {body}
    </section>
  );
};

export default RaceNightsSection;
